import {
  Alert,
  Box,
  Button,
  Dialog,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  Typography,
  useColorScheme,
} from '@mui/material';
import TextField from '@mui/material/TextField';
import {
  editTag,
  getTags,
} from '@pingum/app-state-management/tag/actions';
import { addTag } from '@pingum/app-state-management/tagType/actions';
import PropTypes from 'prop-types';
import * as React from 'react';
import {
  useDispatch,
  useSelector,
} from 'react-redux';

import { Close } from '@mui/icons-material';

const AddEditTagModalComponent = ({
  open,
  onClose,
  onEdit,
  onAdd,
  tag,
}) => {
  const dispatch = useDispatch();
  // React state
  const [name, setName] = React.useState('')
  const [isValid, setIsValid] = React.useState(true);
  const [tagType, setTagType] = React.useState(null);
  const [confirmDelete, setConfirmDelete] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState('')

  React.useEffect(() => {
    if (tag) {
      setName(tag.name);
      setTagType(tag.tagType.id);
      setConfirmDelete(false);
      setErrorMessage('');
    } else {
      setName('');
      setTagType(null);
    }
  }, [tag])

  // Redux state
  const { isEditingTag } = useSelector((state) => state.tag);
  const { isAddingTag } = useSelector((state) => state.tagType);
  const selectedBarn = useSelector((state) => state.barn.selectedBarn);
  const { mode } = useColorScheme();

  const handleNameChange = (e) => {
    setName(e.target.value);
  }

  const saveTag = () => {
    const valid
      = name !== null
      && name !== undefined
      && name.length >= 2
      && name.length <= 50
      && tagType != null;

    if (!valid) {
      setIsValid(false);
      return;
    }

    const addTagRequest = {
      name,
      parentTagId: null,
    };
    if (tag === null) {
      dispatch(addTag(parseInt(tagType, 10), addTagRequest)).then((newTag) => {
        setIsValid(true);
        setName('');
        setTagType(null);
        setIsValid(true);
        onAdd(newTag);
      });
    } else {
      dispatch(editTag(tag.id, {
        name,
        tagTypeId: parseInt(tagType, 10),
      }));
      setTimeout(() => {
        setIsValid(true);
        setName('');
        setTagType(null);
        setIsValid(true);
        onEdit({
          ...tag,
          name,
          tagType: {
            ...tag.tagType,
            id: parseInt(tagType, 10),
          },
        })
      }, 500)
    }
  }

  const removeTag = () => {
    // TODO right now we will only archive the tag, in the future we should delete?
    // Or allow enabling and disabling archived tags
    dispatch(editTag(tag.id, { archived: true }))
    setTimeout(() => {
      setIsValid(true);
      setName('');
      setTagType(null);
      setIsValid(true);
      // refresh global tags
      dispatch(getTags(false, false));
      onEdit({
        ...tag,
        archived: true,
      })
    }, 500)
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <Paper sx={{ overflow: 'hidden' }}>
        <Box
          sx={{
            background: mode === 'light'
              ? 'var(--template-palette-primary-main, #1976d2)'
              : 'var(--template-palette-background-paper)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
          }}
        >
          <Typography
            id="modal-add-tag-title"
            variant="h6"
            component="h2"
            color={mode === 'light'
              ? '#fff'
              : 'inherit'}
          >
            {tag
              ? 'Edit Tag'
              : 'Add Tag'}
          </Typography>
          <IconButton
            onClick={onClose}
            sx={{
              color: mode === 'light'
                ? '#fff'
                : 'inherit',
            }}
          >
            <Close />
          </IconButton>
        </Box>
        <Box sx={{ p: 3 }}>
          <TextField
            error={!isValid && (name.length < 2 || name.length > 50)}
            fullWidth
            helperText={!isValid && 'Tag name must be between 2 and 50 characters'}
            variant="outlined"
            id="tag-name"
            label="Tag Name"
            disabled={isAddingTag || isEditingTag}
            value={name}
            onChange={handleNameChange}
          />
          <FormControl
            error={!isValid && !tagType}
            sx={{
              m: 1,
              width: '100%',
            }}
          >
            <FormLabel id="horse-sex-radio-buttons-group-label">Tag Type</FormLabel>
            <RadioGroup
              row
              aria-labelledby="horse-sex-radio-buttons-group-label"
              name="horse-sex-buttons-group"
              value={tagType}
              disabled={isAddingTag || isEditingTag}
              onChange={(e) => {
                setTagType(e.target.value);
              }}
            >
              <FormControlLabel disabled={isAddingTag || isEditingTag} value={selectedBarn && selectedBarn.barnMetadata && selectedBarn.barnMetadata.tagsTagTypeId} control={<Radio />} label="General" />
              <FormControlLabel disabled={isAddingTag || isEditingTag} value={selectedBarn && selectedBarn.barnMetadata && selectedBarn.barnMetadata.healthTagTypeId} control={<Radio />} label="Health" />
            </RadioGroup>
            <FormHelperText>Use health tags when wanting to map tasks to horse health records</FormHelperText>
          </FormControl>
          <Divider sx={{ my: 2 }} />
          {errorMessage && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage}</Alert>}
          <Box
            sx={{
              justifyContent: 'space-between',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {!!tag && (
              <Button
                onClick={() => {
                  if (confirmDelete) {
                    removeTag();
                  } else {
                    setConfirmDelete(true);
                  }
                }}
                disabled={isEditingTag}
                variant="outlined"
                color="error"
              >
                {confirmDelete
                  ? 'Confirm Delete'
                  : 'Delete'}
              </Button>
            )}
            <Button onClick={saveTag} color="primary" variant="contained" disabled={isAddingTag || isEditingTag} loading={isAddingTag || isEditingTag}>Save</Button>
          </Box>
        </Box>
      </Paper>
    </Dialog>
  );
};

AddEditTagModalComponent.propTypes = {
  onAdd: PropTypes.func,
  onEdit: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  tag: PropTypes.object,
  open: PropTypes.bool,
};

AddEditTagModalComponent.defaultProps = {
  onAdd: () => { },
  onEdit: () => { },
  tag: null,
  open: false,
};

export default AddEditTagModalComponent;
